/**
 * PricingConfigTab — central editor for plan prices, trials and per-plan
 * promotional discounts. Saves to /api/pricing (separate from landing
 * /api/landing/content). Drives Stripe checkout, onboarding cards,
 * landing page price overlay and welcome emails.
 */
import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Loader2, Tag, Sparkles, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_META = {
  weekly: { title: 'Weekly Plan', hint: 'Billed weekly. Lowest commitment — great for impulse trials.' },
  monthly: { title: 'Monthly Plan', hint: 'Billed monthly. No trial.' },
  annual: { title: 'Annual Plan', hint: 'Charged at trial amount up-front, then renews at base price.' },
  lifetime: { title: 'Lifetime Deal', hint: 'One-time payment, no recurrence.' },
};

const PLAN_ORDER = ['weekly', 'monthly', 'annual', 'lifetime'];

export const PricingConfigTab = forwardRef(function PricingConfigTab(_, ref) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pricing`);
      setConfig(data);
    } catch (e) {
      toast.error('Failed to load pricing config');
    } finally {
      setLoading(false);
    }
  };

  const updatePlan = (planId, patch) => {
    setConfig(prev => ({
      ...prev,
      plans: {
        ...prev.plans,
        [planId]: { ...prev.plans[planId], ...patch },
      },
    }));
  };

  const updateBanner = (patch) => {
    setConfig(prev => ({
      ...prev,
      landing_banner: { ...(prev.landing_banner || {}), ...patch },
    }));
  };

  const updateDiscount = (planId, patch) => {
    setConfig(prev => ({
      ...prev,
      plans: {
        ...prev.plans,
        [planId]: {
          ...prev.plans[planId],
          discount: { ...(prev.plans[planId].discount || {}), ...patch },
        },
      },
    }));
  };

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { plans: {}, landing_banner: {
        enabled: !!(config.landing_banner?.enabled),
        custom_text: config.landing_banner?.custom_text || '',
        cta_text: config.landing_banner?.cta_text || 'View plans',
      } };
      for (const pid of PLAN_ORDER) {
        const p = config.plans[pid] || {};
        payload.plans[pid] = {
          label: p.label,
          base_amount: parseFloat(p.base_amount) || 0,
          interval: p.interval,
          trial_amount: p.trial_amount === null || p.trial_amount === '' || p.trial_amount === undefined
            ? null : parseFloat(p.trial_amount),
          trial_days: parseInt(p.trial_days || 0, 10) || 0,
          trial_enabled: p.trial_enabled !== false,
          intro_expires_at: p.intro_expires_at || null,
          annual_equivalent: p.annual_equivalent === null || p.annual_equivalent === '' || p.annual_equivalent === undefined
            ? null : parseFloat(p.annual_equivalent),
          monthly_equivalent: p.monthly_equivalent === null || p.monthly_equivalent === '' || p.monthly_equivalent === undefined
            ? null : parseFloat(p.monthly_equivalent),
          discount: {
            enabled: !!(p.discount?.enabled),
            percent: parseFloat(p.discount?.percent) || 0,
            label: p.discount?.label || '',
          },
        };
      }
      const { data } = await axios.put(`${API}/pricing`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfig(data);
      toast.success('Pricing saved – changes are live everywhere');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  // Expose save() so the parent's "Save Changes" button can also trigger it.
  useImperativeHandle(ref, () => save);

  if (loading || !config) {
    return (
      <Card><CardContent className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></CardContent></Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-emerald-600" /> Plans &amp; Billing
          </CardTitle>
          <CardDescription>
            Single source of truth for plan prices and promotional discounts. Changes here propagate
            instantly to Stripe checkout, the onboarding plan cards, the landing page and welcome emails.
            <strong className="block mt-1 text-amber-700">Currency is fixed to EUR (€).</strong>
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Landing hero promo banner */}
      <Card data-testid="landing-banner-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Active Promo Banner — Landing Hero</span>
            <Switch
              checked={!!config.landing_banner?.enabled}
              onCheckedChange={(v) => updateBanner({ enabled: v })}
              data-testid="landing-banner-toggle"
            />
          </CardTitle>
          <CardDescription>
            When ON, an amber pill banner appears on the landing hero with sparkles icon and a CTA that scrolls to the
            pricing section. Only renders if at least one plan has an active discount, OR you provide custom text below.
          </CardDescription>
        </CardHeader>
        {config.landing_banner?.enabled && (
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Custom Banner Text (optional)</Label>
                <Input
                  value={config.landing_banner?.custom_text || ''}
                  onChange={(e) => updateBanner({ custom_text: e.target.value })}
                  placeholder="Leave blank to auto-generate from active discounts"
                  data-testid="landing-banner-text-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Blank = auto-generates e.g. <em>"Black Friday 30% off — limited time on the annual plan"</em>
                </p>
              </div>
              <div>
                <Label>CTA Text</Label>
                <Input
                  value={config.landing_banner?.cta_text || ''}
                  onChange={(e) => updateBanner({ cta_text: e.target.value })}
                  placeholder="View plans"
                  data-testid="landing-banner-cta-input"
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {PLAN_ORDER.map(pid => {
        const p = config.plans?.[pid] || {};
        const meta = PLAN_META[pid];
        const trialActive = pid === 'annual' && p.trial_enabled !== false && p.trial_amount != null && p.trial_amount !== '';
        const rawCharge = trialActive ? p.trial_amount : p.base_amount;
        const chargeNum = parseFloat(rawCharge) || 0;
        const eff = p.discount?.enabled && p.discount?.percent
          ? Math.round(chargeNum * (1 - (parseFloat(p.discount.percent) || 0) / 100) * 100) / 100
          : chargeNum;
        return (
          <Card key={pid} data-testid={`pricing-plan-${pid}`}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{meta.title}</span>
                {p.discount?.enabled && (
                  <span className="text-xs font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> {p.discount?.percent || 0}% off
                  </span>
                )}
              </CardTitle>
              <CardDescription className="flex items-center justify-between gap-2 flex-wrap">
                <span>{meta.hint}</span>
                <span className="text-xs font-medium text-gray-500">
                  Charge at checkout: <strong className="text-gray-900">€{eff.toFixed(2)}</strong>
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Display Label</Label>
                  <Input
                    value={p.label || ''}
                    onChange={e => updatePlan(pid, { label: e.target.value })}
                    data-testid={`plan-${pid}-label-input`}
                  />
                </div>
                <div>
                  <Label>Base Amount (€)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={p.base_amount ?? ''}
                    onChange={e => updatePlan(pid, { base_amount: e.target.value })}
                    data-testid={`plan-${pid}-base-amount-input`}
                  />
                </div>
                {pid === 'annual' && (
                  <>
                    <div className="sm:col-span-2 border border-blue-200 rounded-lg p-3 bg-blue-50/40 -mt-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <Label className="text-blue-900 font-semibold">Enable Trial</Label>
                          <p className="text-xs text-blue-800/70 mt-0.5">
                            When enabled, checkout charges the <strong>Trial Amount</strong> for the first <strong>Trial Days</strong>,
                            then the base amount renews after. Disable to charge the base amount directly at checkout (no trial period).
                            Set Trial Amount to <strong>0</strong> for a free trial — Stripe will skip the first charge.
                          </p>
                        </div>
                        <Switch
                          checked={p.trial_enabled !== false}
                          onCheckedChange={(v) => updatePlan(pid, { trial_enabled: v })}
                          data-testid={`plan-${pid}-trial-toggle`}
                        />
                      </div>
                    </div>
                    {p.trial_enabled !== false && (
                      <>
                        {/* Quick preset buttons — one-click intro patterns */}
                        <div className="sm:col-span-2 -mb-1">
                          <Label className="text-xs uppercase tracking-wider text-gray-500">Quick presets</Label>
                          <div className="mt-1.5 flex flex-wrap gap-2">
                            {[
                              { id: 'paid-week',    label: '1 week for \u20AC1.99',  amt: 1.99, days: 7  },
                              { id: 'paid-month',   label: '30 days for \u20AC1.99', amt: 1.99, days: 30 },
                              { id: 'free-week',    label: 'Free 7-day trial',       amt: 0,    days: 7  },
                              { id: 'free-2weeks',  label: 'Free 14-day trial',      amt: 0,    days: 14 },
                              { id: 'free-month',   label: 'Free 30-day trial',      amt: 0,    days: 30 },
                            ].map(preset => {
                              const currentAmt = parseFloat(p.trial_amount) || 0;
                              const currentDays = parseInt(p.trial_days || 0, 10) || 0;
                              const active = currentAmt === preset.amt && currentDays === preset.days;
                              return (
                                <button
                                  key={preset.id}
                                  type="button"
                                  onClick={() => updatePlan(pid, { trial_amount: preset.amt, trial_days: preset.days })}
                                  className={`px-3 py-1.5 text-xs font-semibold rounded-full border-2 transition-all ${
                                    active
                                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                                      : 'border-gray-200 bg-white text-gray-700 hover:border-emerald-300 hover:bg-emerald-50/40'
                                  }`}
                                  data-testid={`plan-${pid}-preset-${preset.id}`}
                                >
                                  {preset.label}
                                </button>
                              );
                            })}
                          </div>
                          <p className="text-[11px] text-gray-500 mt-1.5">
                            Presets fill in Trial Amount + Trial Days below. Tweak the values for custom offers.
                          </p>
                        </div>
                        <div>
                          <Label>Trial Amount (€)</Label>
                          <Input
                            type="number" step="0.01" min="0"
                            value={p.trial_amount ?? ''}
                            onChange={e => updatePlan(pid, { trial_amount: e.target.value })}
                            placeholder="e.g. 1.99 (0 = free trial)"
                            data-testid={`plan-${pid}-trial-amount-input`}
                          />
                        </div>
                        <div>
                          <Label>Trial Days</Label>
                          <Input
                            type="number" min="0"
                            value={p.trial_days ?? ''}
                            onChange={e => updatePlan(pid, { trial_days: e.target.value })}
                            placeholder="e.g. 7"
                            data-testid={`plan-${pid}-trial-days-input`}
                          />
                        </div>
                        {/* Limited-time offer expiry — when set, the intro pill shows a countdown
                            ("Offer ends in 3 days") and the backend refuses to apply the intro
                            coupon / free-trial after the expiry moment. */}
                        <div className="sm:col-span-2 border border-amber-200/60 rounded-lg p-3 bg-amber-50/30 -mt-1">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div>
                              <Label className="text-amber-900 font-semibold">Limit this offer (optional)</Label>
                              <p className="text-xs text-amber-800/70 mt-0.5">
                                When set, the intro pill displays an "Offer ends in N days" countdown on the landing card,
                                and the backend auto-disables the intro at this moment. Leave blank for an evergreen offer.
                              </p>
                            </div>
                            <Switch
                              checked={!!p.intro_expires_at}
                              onCheckedChange={(v) => {
                                if (v) {
                                  // Pre-fill 14 days from now in local datetime-local format
                                  const d = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
                                  const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60 * 1000)
                                    .toISOString().slice(0, 16);
                                  updatePlan(pid, { intro_expires_at: iso });
                                } else {
                                  updatePlan(pid, { intro_expires_at: '' });
                                }
                              }}
                              data-testid={`plan-${pid}-intro-expiry-toggle`}
                            />
                          </div>
                          {p.intro_expires_at && (
                            <>
                              <Input
                                type="datetime-local"
                                value={(p.intro_expires_at || '').slice(0, 16)}
                                onChange={e => updatePlan(pid, { intro_expires_at: e.target.value })}
                                data-testid={`plan-${pid}-intro-expiry-input`}
                              />
                              <p className="text-[11px] text-amber-700 mt-1">
                                {(() => {
                                  try {
                                    const d = new Date(p.intro_expires_at);
                                    if (isNaN(d.getTime())) return 'Invalid date';
                                    const days = Math.ceil((d.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
                                    if (days <= 0) return 'Offer has already expired \u2014 intro will not be applied at checkout.';
                                    if (days === 1) return 'Offer ends within 24 hours.';
                                    return `Offer ends in ${days} days (${d.toLocaleString()}).`;
                                  } catch {
                                    return '';
                                  }
                                })()}
                              </p>
                            </>
                          )}
                        </div>
                      </>
                    )}
                    <div>
                      <Label>Monthly Equivalent (€, display only)</Label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={p.monthly_equivalent ?? ''}
                        onChange={e => updatePlan(pid, { monthly_equivalent: e.target.value })}
                        placeholder="e.g. 6.99"
                        data-testid={`plan-${pid}-monthly-equiv-input`}
                      />
                    </div>
                  </>
                )}
                {pid === 'monthly' && (
                  <div>
                    <Label>Annual Equivalent (€, display only)</Label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={p.annual_equivalent ?? ''}
                      onChange={e => updatePlan(pid, { annual_equivalent: e.target.value })}
                      placeholder="e.g. 107.88"
                      data-testid={`plan-${pid}-annual-equiv-input`}
                    />
                  </div>
                )}
              </div>

              {/* Discount block */}
              <div className="border border-amber-200 rounded-lg p-4 bg-amber-50/50 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-amber-900 font-semibold">Promotional Discount</Label>
                    <p className="text-xs text-amber-800/70 mt-0.5">
                      Toggle on to apply a real discount at Stripe checkout. The discount label
                      appears on the product line and in plan card UI.
                    </p>
                  </div>
                  <Switch
                    checked={!!p.discount?.enabled}
                    onCheckedChange={(v) => updateDiscount(pid, { enabled: v })}
                    data-testid={`plan-${pid}-discount-toggle`}
                  />
                </div>
                {p.discount?.enabled && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Discount %</Label>
                      <Input
                        type="number" min="0" max="95" step="1"
                        value={p.discount?.percent ?? 0}
                        onChange={e => updateDiscount(pid, { percent: e.target.value })}
                        data-testid={`plan-${pid}-discount-percent-input`}
                      />
                    </div>
                    <div>
                      <Label>Promo Label</Label>
                      <Input
                        value={p.discount?.label || ''}
                        onChange={e => updateDiscount(pid, { label: e.target.value })}
                        placeholder="e.g. Black Friday 30% off"
                        data-testid={`plan-${pid}-discount-label-input`}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <OfferBlastCard annualPlan={config.plans?.annual} />
      <WinbackBlastCard />

      <Card>
        <CardContent className="p-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 flex items-center gap-2"
            data-testid="pricing-save-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : 'Save Pricing'}
          </button>
        </CardContent>
      </Card>
    </div>
  );
});


/**
 * OfferBlastCard — one-tap "send a reminder to all free-tier coaches" button
 * that unlocks within 72 hours of the active intro's expiry. Includes a
 * preview (eligible count, already-sent count) and a two-step confirmation.
 */
function OfferBlastCard({ annualPlan }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [recentOnly, setRecentOnly] = useState(true);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/stripe/admin/offer-blast-preview`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { recent_only: recentOnly },
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load blast preview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPreview(); }, [annualPlan?.intro_expires_at, annualPlan?.trial_amount, annualPlan?.trial_days, annualPlan?.trial_enabled, recentOnly]); // refresh when admin tweaks intro or filter

  const send = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/stripe/admin/offer-blast-send`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        params: { recent_only: recentOnly },
      });
      toast.success(`Sent ${data.sent} email${data.sent === 1 ? '' : 's'}. Skipped ${data.skipped_already_sent} already-emailed coaches.`);
      setConfirmOpen(false);
      fetchPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Blast failed');
    } finally {
      setSending(false);
    }
  };

  if (loading && !preview) {
    return (
      <Card data-testid="offer-blast-card">
        <CardContent className="p-4 flex items-center justify-center text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Checking offer blast status…
        </CardContent>
      </Card>
    );
  }

  if (!preview) return null;

  return (
    <Card data-testid="offer-blast-card" className="border-amber-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-amber-900">
          <Send className="w-5 h-5" /> One-tap Offer Reminder Blast
        </CardTitle>
        <CardDescription>
          Sends a "your {preview.intro_label || 'intro'} ends soon" email to every free-tier coach who hasn't paid yet.
          Unlocks within 72 hours of the active intro's expiry (set above). Idempotent — same coach won't be emailed twice for the same expiry window.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          <Stat label="Offer" value={preview.intro_label || '\u2014'} testid="blast-stat-intro" />
          <Stat label="Hours left" value={preview.hours_left ?? '\u2014'} testid="blast-stat-hours" />
          <Stat label="Eligible" value={preview.eligible_count} testid="blast-stat-eligible" />
          <Stat label="Already sent" value={preview.already_sent_in_window} testid="blast-stat-sent" />
        </div>
        <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-amber-200/60 bg-amber-50/30">
          <div>
            <Label className="text-amber-900 font-semibold text-sm">Only coaches active in last 30 days</Label>
            <p className="text-[11px] text-amber-800/70 mt-0.5">
              Skips ghost accounts — almost always lifts open + click-through rates. Recommended on.
            </p>
          </div>
          <Switch
            checked={recentOnly}
            onCheckedChange={setRecentOnly}
            data-testid="blast-recent-only-toggle"
          />
        </div>
        {!preview.can_send && preview.reason && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="blast-reason">
            {preview.reason}
          </div>
        )}
        {!confirmOpen ? (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={!preview.can_send}
            className="w-full sm:w-auto px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="blast-send-btn"
          >
            <Send className="w-4 h-4" /> Send blast to {Math.max(0, preview.eligible_count - preview.already_sent_in_window)} coach{(preview.eligible_count - preview.already_sent_in_window) === 1 ? '' : 'es'}
          </button>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2 p-3 rounded-lg bg-amber-50 border-2 border-amber-300" data-testid="blast-confirm-row">
            <span className="text-sm text-amber-900 font-semibold flex-1">Are you sure? This sends real emails.</span>
            <button
              type="button"
              onClick={send}
              disabled={sending}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="blast-confirm-yes"
            >
              {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Yes, send</>}
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={sending}
              className="px-4 py-2 bg-white border border-amber-300 hover:bg-amber-100 text-amber-800 text-sm font-semibold rounded-lg"
              data-testid="blast-confirm-cancel"
            >
              Cancel
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="p-3 rounded-lg border border-amber-100 bg-amber-50/40">
      <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-1">{label}</p>
      <p className="text-sm font-bold text-amber-900 break-words" data-testid={testid}>{value}</p>
    </div>
  );
}


/**
 * WinbackBlastCard — sends a soft "we miss you" email to coaches inactive
 * for N+ days. Independent of the intro offer; uses its own dedupe window
 * (60 days) so dormant coaches aren't re-emailed every campaign.
 */
function WinbackBlastCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [inactiveDays, setInactiveDays] = useState(30);

  const fetchPreview = async (days) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/stripe/admin/winback-preview`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { inactive_days: days ?? inactiveDays },
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load win-back preview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPreview(inactiveDays); }, [inactiveDays]);

  const send = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/stripe/admin/winback-send`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        params: { inactive_days: inactiveDays },
      });
      toast.success(`Win-back: sent ${data.sent}, skipped ${data.skipped_already_sent} already-emailed coaches.`);
      setConfirmOpen(false);
      fetchPreview(inactiveDays);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Win-back blast failed');
    } finally {
      setSending(false);
    }
  };

  if (loading && !preview) {
    return (
      <Card data-testid="winback-card">
        <CardContent className="p-4 flex items-center justify-center text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Checking win-back status…
        </CardContent>
      </Card>
    );
  }

  if (!preview) return null;

  return (
    <Card data-testid="winback-card" className="border-blue-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-900">
          <Send className="w-5 h-5" /> Win-back Blast
        </CardTitle>
        <CardDescription>
          Sends a soft "we miss you" email to coaches who haven't logged in for the threshold below.
          De-duped — the same coach won't be win-back-emailed more than once every {preview.dedupe_days} days.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Label className="text-blue-900 font-semibold text-sm whitespace-nowrap">Inactive for at least</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="7"
              max="365"
              value={inactiveDays}
              onChange={e => setInactiveDays(Math.max(7, Math.min(365, parseInt(e.target.value || 30, 10))))}
              className="w-24"
              data-testid="winback-days-input"
            />
            <span className="text-sm text-blue-800">days</span>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {[30, 60, 90, 180].map(d => (
              <button
                key={d}
                type="button"
                onClick={() => setInactiveDays(d)}
                className={`px-2.5 py-1 text-xs font-semibold rounded-full border-2 transition-all ${
                  inactiveDays === d
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300'
                }`}
                data-testid={`winback-preset-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center">
          <BlueStat label="Inactive >=" value={`${preview.inactive_days}d`} testid="winback-stat-days" />
          <BlueStat label="Eligible" value={preview.eligible_count} testid="winback-stat-eligible" />
          <BlueStat label="Recently emailed" value={preview.already_sent_recent} testid="winback-stat-sent" />
        </div>

        {!preview.can_send && preview.reason && (
          <div className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid="winback-reason">
            {preview.reason}
          </div>
        )}

        {!confirmOpen ? (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={!preview.can_send}
            className="w-full sm:w-auto px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="winback-send-btn"
          >
            <Send className="w-4 h-4" /> Send win-back to {preview.eligible_count} coach{preview.eligible_count === 1 ? '' : 'es'}
          </button>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2 p-3 rounded-lg bg-blue-50 border-2 border-blue-300" data-testid="winback-confirm-row">
            <span className="text-sm text-blue-900 font-semibold flex-1">Send win-back emails now? Real emails will go out.</span>
            <button
              type="button"
              onClick={send}
              disabled={sending}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="winback-confirm-yes"
            >
              {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Yes, send</>}
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={sending}
              className="px-4 py-2 bg-white border border-blue-300 hover:bg-blue-100 text-blue-800 text-sm font-semibold rounded-lg"
              data-testid="winback-confirm-cancel"
            >
              Cancel
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BlueStat({ label, value, testid }) {
  return (
    <div className="p-3 rounded-lg border border-blue-100 bg-blue-50/40">
      <p className="text-[10px] uppercase tracking-wider text-blue-700 font-bold mb-1">{label}</p>
      <p className="text-sm font-bold text-blue-900 break-words" data-testid={testid}>{value}</p>
    </div>
  );
}
